import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModelSortDirection, SingleUnit } from '../../types/schema';
import { useUserContext } from '../../contexts/UserContext';
import { hasPermission, Permission } from '../../utils/permissions';
import {
    PMConfiguration,
    PMNotificationSetting,
    PMReport,
    PMStatus,
} from '../../types/pmTypes';
import {
    enrollUnitInPM,
    fetchPMConfigurationsByUnit,
} from '../../graphql/pmQueries';
import { pmSchedulingUM } from '../pages-equipment/equipmentFieldUtils';
import { calculatePMStatus, calculateRemainingUM, getNextServiceInfo, getPrimaryServiceLevel, serviceLevelsOnSchedulingScale } from '../../utils/pmUtils';
import { authClient } from '../../utils/amplifyClient';
import { displayUnitOfMeasure, normalizeUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import { listPMReportByUnitIdAndCreatedAt } from '../../graphql/queries';
import { useCompanyUnitsOfMeasure } from '../../hooks/useCompanyUnitsOfMeasure';
import { pmEquipmentConfigurationPath } from '../../utils/pmRoutes';
import { Btn, Select } from '../ui';
import '../../css/SingleUnitPMTab.css';

interface SingleUnitPMTabProps {
    unit: SingleUnit;
}

/**
 * PM Tab for the SingleUnit page.
 * Shows PM schedule overview, next scheduled service, notification settings,
 * and action buttons when enrolled. Shows enrollment option when not enrolled.
 */
export const SingleUnitPMTab = ({ unit }: SingleUnitPMTabProps) => {
    const navigate = useNavigate();
    const { company, companyOwner, companyUser, businessAdmin } = useUserContext();
    const canManage = hasPermission(companyOwner, companyUser, Permission.MANAGE_PM, businessAdmin);

    const [config, setConfig] = useState<PMConfiguration | null>(null);
    const [latestReport, setLatestReport] = useState<PMReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [enrolling, setEnrolling] = useState(false);
    const [selectedUM, setSelectedUM] = useState(() => normalizeUnitOfMeasure(unit.unitOfMeasure || 'hours'));
    const [error, setError] = useState<string | null>(null);

    const companyId = (company?.id as string) || companyUser?.companyId || companyOwner?.companyId || '';
    const { catalogOptions } = useCompanyUnitsOfMeasure(companyId);
    const unitOptions =
        catalogOptions.length > 0
            ? catalogOptions
            : [{ value: selectedUM, label: displayUnitOfMeasure(selectedUM) }];

    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------

    const loadPMData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const configs = await fetchPMConfigurationsByUnit(unit.id);
            const raw = configs?.[0];
            if (!raw) {
                setConfig(null);
                setLatestReport(null);
                return;
            }

            const [serviceLevelsResult, partsResult, notificationsResult] = await Promise.all([
                authClient.models.PMServiceLevel.listPMServiceLevelByPmConfigurationId({ pmConfigurationId: raw.id }),
                authClient.models.PMPartsAndProcedure.listPMPartsAndProcedureByPmConfigurationId({ pmConfigurationId: raw.id }),
                authClient.models.PMNotificationSetting.listPMNotificationSettingByPmConfigurationId({ pmConfigurationId: raw.id }),
            ]);

            const normalized: PMConfiguration = {
                id: raw.id,
                companyId: raw.companyId,
                unitId: raw.unitId,
                unitOfMeasure: raw.unitOfMeasure,
                dueSoonPercentage: raw.dueSoonPercentage ?? 0.1,
                serviceLevels: serviceLevelsResult.data ?? [],
                partsAndProcedures: partsResult.data ?? [],
                laborCategoryId: raw.laborCategoryId ?? null,
                notificationSettings: (notificationsResult.data ?? []) as PMNotificationSetting[],
                createdAt: raw.createdAt,
                updatedAt: raw.updatedAt,
            };
            setConfig(normalized);

            try {
                const reportResult = await authClient.graphql({
                    query: listPMReportByUnitIdAndCreatedAt,
                    variables: {
                        unitId: unit.id,
                        sortDirection: 'DESC' as ModelSortDirection,
                        limit: 1,
                    },
                });
                const reports = reportResult.data?.listPMReportByUnitIdAndCreatedAt?.items ?? [];
                setLatestReport(reports.length > 0 ? (reports[0] as PMReport) : null);
            } catch {
                setLatestReport(null);
            }
        } catch (err) {
            console.error('Failed to load PM data:', err);
            setError('Failed to load PM configuration.');
        } finally {
            setLoading(false);
        }
    }, [unit.id]);

    useEffect(() => {
        loadPMData();
    }, [loadPMData]);

    // -----------------------------------------------------------------------
    // Enrollment handler
    // -----------------------------------------------------------------------

    const handleEnroll = async () => {
        if (!companyId) return;
        setEnrolling(true);
        setError(null);
        try {
            await enrollUnitInPM({
                companyId,
                unitId: unit.id,
                unitOfMeasure: selectedUM,
                dueSoonPercentage: 0.1,
            });
            await loadPMData();
        } catch (err) {
            console.error('Failed to enroll unit:', err);
            setError('Failed to enroll unit in PM module.');
        } finally {
            setEnrolling(false);
        }
    };

    // -----------------------------------------------------------------------
    // Loading state
    // -----------------------------------------------------------------------

    if (loading) {
        return (
            <div className="pm-tab-loading">
                <p>Loading PM data...</p>
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Not enrolled state
    // -----------------------------------------------------------------------

    if (!config) {
        const pmEnabled = !!(company?.pmModuleEnrollment);

        return (
            <div className="pm-tab-not-enrolled">
                <div className="pm-tab-not-enrolled-icon">⚙️</div>
                <h2>This unit is not enrolled in the PM module</h2>
                {pmEnabled ? (
                    <div className="pm-tab-enroll-form">
                        <p>Enroll this unit to start tracking preventive maintenance schedules.</p>
                        <div className="pm-tab-enroll-field">
                            <label htmlFor="pm-um-select">Unit of Measure</label>
                            <Select
                                inputId="pm-um-select"
                                ariaLabel="Unit of Measure"
                                value={selectedUM}
                                onChange={setSelectedUM}
                                options={unitOptions}
                            />
                        </div>
                        {error && <p className="pm-tab-error">{error}</p>}
                        <Btn
                            variant="primary"
                            buttonText={enrolling ? 'Enrolling...' : 'Enroll in PM Module'}
                            buttonDisabled={enrolling}
                            buttonOnClick={handleEnroll}
                        />
                    </div>
                ) : (
                    <p className="pm-tab-disabled-msg">
                        The PM module is not enabled for your company. Contact your administrator to activate it.
                    </p>
                )}
            </div>
        );
    }

    // -----------------------------------------------------------------------
    // Enrolled state — compute derived values
    // -----------------------------------------------------------------------

    const serviceLevels = serviceLevelsOnSchedulingScale(
        config.serviceLevels ?? [],
        unit.replacedUMCounterValue,
    );
    const notificationSettings = config.notificationSettings ?? [];
    const currentUM = pmSchedulingUM(unit);
    const dueSoonPct = config.dueSoonPercentage ?? 0.1;
    const um = config.unitOfMeasure || 'hours';
    const umLabel = displayUnitOfMeasure(um);

    const nextService = getNextServiceInfo(currentUM, serviceLevels);
    const primaryLevel = getPrimaryServiceLevel(serviceLevels);
    const highestDueLevel = nextService?.dueLevels.length
        ? nextService.dueLevels[nextService.dueLevels.length - 1]
        : null;
    const dueLevelNames = nextService?.dueLevels
        .map((sl) => sl.levelIdentifier)
        .filter(Boolean)
        .join(', ');
    const nextStatus = nextService
        ? calculatePMStatus({
            currentUM,
            targetUM: nextService.targetUM,
            dueSoonPercentage: dueSoonPct,
            intervalUM: primaryLevel?.intervalValue,
        })
        : PMStatus.SCHEDULED;
    const nextRemaining = nextService ? calculateRemainingUM(currentUM, nextService.targetUM) : 0;

    // UM frequency string (e.g., "500 / 1000 / 2000 hrs")
    const umFrequency = serviceLevels.length > 0
        ? serviceLevels
            .sort((a, b) => a.intervalValue - b.intervalValue)
            .map((sl) => sl.intervalValue.toLocaleString())
            .join(' / ') + ` ${umLabel}`
        : '—';

    // First notification setting for display
    const primaryNotification: PMNotificationSetting | null = notificationSettings[0] ?? null;

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US');
    };

    const getStatusClass = (status: PMStatus) => {
        switch (status) {
            case PMStatus.OVERDUE: return 'overdue';
            case PMStatus.DUE_SOON: return 'due-soon';
            default: return 'scheduled';
        }
    };

    return (
        <div className="pm-tab-content">
            {error && <p className="pm-tab-error">{error}</p>}

            {/* PM Schedule Overview */}
            <div className="pm-tab-section pm-schedule-overview">
                <h2>PM Schedule Overview</h2>
                <table className="pm-tab-overview-table">
                    <tbody>
                        <tr>
                            <td className="pm-tab-overview-label">UM Frequency</td>
                            <td className="pm-tab-overview-value">{umFrequency}</td>
                        </tr>
                        <tr>
                            <td className="pm-tab-overview-label">Last PM Service Date</td>
                            <td className="pm-tab-overview-value">{formatDate(latestReport?.serviceDate)}</td>
                        </tr>
                        <tr>
                            <td className="pm-tab-overview-label">Last PM UM</td>
                            <td className="pm-tab-overview-value">
                                {latestReport?.actualUM != null ? `${latestReport.actualUM.toLocaleString()} ${umLabel}` : '—'}
                            </td>
                        </tr>
                        <tr>
                            <td className="pm-tab-overview-label">Last PM Service Level</td>
                            <td className="pm-tab-overview-value">{latestReport?.serviceLevelName || '—'}</td>
                        </tr>
                        <tr>
                            <td className="pm-tab-overview-label">Next PM Service Interval UM</td>
                            <td className="pm-tab-overview-value">
                                {primaryLevel ? `${primaryLevel.intervalValue.toLocaleString()} ${umLabel}` : '—'}
                            </td>
                        </tr>
                        <tr>
                            <td className="pm-tab-overview-label">Next Scheduled PM Level</td>
                            <td className="pm-tab-overview-value">{dueLevelNames || '—'}</td>
                        </tr>
                        <tr>
                            <td className="pm-tab-overview-label">PM Service Due Notification Threshold</td>
                            <td className="pm-tab-overview-value">
                                {primaryNotification ? `${primaryNotification.emailThresholdUM} ${umLabel}` : '—'}
                            </td>
                        </tr>
                        <tr>
                            <td className="pm-tab-overview-label">Standard Notification Email List</td>
                            <td className="pm-tab-overview-value">
                                {primaryNotification?.recipients?.join(', ') || '—'}
                            </td>
                        </tr>
                        <tr>
                            <td className="pm-tab-overview-label">Overdue Notification Email List</td>
                            <td className="pm-tab-overview-value">
                                {primaryNotification?.recipients?.join(', ') || '—'}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Action Buttons */}
            <div className="pm-tab-actions">
                <Btn
                    variant="secondary-outlined"
                    buttonText="Open PM Configuration"
                    buttonOnClick={() => navigate(pmEquipmentConfigurationPath(unit.id))}
                />
                <Btn
                    variant="primary"
                    buttonText="Record PM Service"
                    buttonDisabled={!nextService?.dueLevels.length}
                    buttonOnClick={() => {
                        if (nextService?.dueLevels.length) {
                            navigate(
                                `/pm-management/inspect/${unit.id}/${nextService.dueLevels.map((sl) => sl.id).join(',')}`,
                            );
                        }
                    }}
                />
            </div>
            <div className="pm-tab-status-indicator">
                Status: <span className={`pm-tab-status-badge ${getStatusClass(nextStatus)}`}>{nextStatus}</span>
            </div>

            {/* Next Scheduled Service */}
            {nextService && (
                <div className="pm-tab-section pm-next-service">
                    <h2>Next Scheduled Service</h2>
                    <div className="pm-tab-next-service-grid">
                        <div className="pm-tab-detail-row">
                            <span className="pm-tab-detail-label">Service Level</span>
                            <span className="pm-tab-detail-value">
                                {dueLevelNames || '—'}
                                {highestDueLevel?.description ? ` – ${highestDueLevel.description}` : ''}
                            </span>
                        </div>
                        <div className="pm-tab-detail-row">
                            <span className="pm-tab-detail-label">Last PM UM</span>
                            <span className="pm-tab-detail-value">
                                {latestReport?.actualUM != null ? `${latestReport.actualUM.toLocaleString()} ${umLabel}` : '—'}
                            </span>
                        </div>
                        <div className="pm-tab-detail-row">
                            <span className="pm-tab-detail-label">Next Service Target</span>
                            <span className="pm-tab-detail-value">{nextService.targetUM.toLocaleString()} {um}</span>
                        </div>
                        <div className="pm-tab-detail-row">
                            <span className="pm-tab-detail-label">Current UM</span>
                            <span className="pm-tab-detail-value">{currentUM.toLocaleString()} {um}</span>
                        </div>
                        <div className="pm-tab-detail-row">
                            <span className="pm-tab-detail-label">Remaining</span>
                            <span className="pm-tab-detail-value">{nextRemaining.toLocaleString()} {um}</span>
                        </div>
                        <div className="pm-tab-detail-row">
                            <span className="pm-tab-detail-label">Status</span>
                            <span className={`pm-tab-status-badge ${getStatusClass(nextStatus)}`}>{nextStatus}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Settings */}
            <div className="pm-tab-section pm-notification-settings">
                <div className="pm-tab-section-header">
                    <h2>Notification Settings</h2>
                    {canManage && (
                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonText="Edit"
                            buttonOnClick={() => navigate(pmEquipmentConfigurationPath(unit.id))}
                        />
                    )}
                </div>
                {primaryNotification ? (
                    <div className="pm-tab-next-service-grid">
                        <div className="pm-tab-detail-row">
                            <span className="pm-tab-detail-label">Service Notification Threshold</span>
                            <span className="pm-tab-detail-value">
                                {primaryNotification.emailThresholdUM} {um} before service
                            </span>
                        </div>
                        <div className="pm-tab-detail-row">
                            <span className="pm-tab-detail-label">Standard Notification Email</span>
                            <span className="pm-tab-detail-value">
                                {primaryNotification.recipients?.join(', ') || '—'}
                            </span>
                        </div>
                        <div className="pm-tab-detail-row">
                            <span className="pm-tab-detail-label">Overdue Notification Email</span>
                            <span className="pm-tab-detail-value">
                                {primaryNotification.recipients?.join(', ') || '—'}
                            </span>
                        </div>
                    </div>
                ) : (
                    <p className="pm-tab-empty-msg">No notification settings configured.</p>
                )}
            </div>
        </div>
    );
};
